// Dialogues de l'éditeur de devis : ligne (création / modification), catalogue, exigences non chiffrées, dérogation, variante.
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowRightLeft, ArrowUp, RefreshCw, Search, Trash2 } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { date, pluriel } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, Dialogue, EtatErreur, EtatVide, Source } from '../../components/ui.tsx';
import { BadgeStatutExigence } from '../../components/statuts.tsx';
import {
  BadgesAlertePrix, libelleMiseAJour, LIBELLES_MOTIFS, OPTIONS_SECTIONS, saisieValide, sourcePrix, versSaisie, type Ligne, type Section, type VueRevision,
} from './commun.tsx';
import { DetailPrix, ListeComparaison, type PrixCatalogue } from './tarifs.tsx';

// ─── Ligne : création libre ou modification complète ───
interface FormLigne {
  section: Section; label: string; manufacturer: string; reference: string; quantity: string; unit: string;
  unitCost: string; laborHours: string; laborRateCode: string; rateOverride: string;
}
const vide: FormLigne = { section: 'materiel', label: '', manufacturer: '', reference: '', quantity: '1', unit: 'u', unitCost: '',
  laborHours: '0', laborRateCode: '', rateOverride: '' };

export function DialogueLigne({ ouvert, fermer, vue, ligne, enregistrer, supprimer, deplacer, actualiser }: {
  ouvert: boolean; fermer: () => void; vue: VueRevision; ligne: Ligne | null;
  enregistrer: (corps: Record<string, unknown>) => Promise<unknown>;
  supprimer?: () => Promise<unknown>; deplacer?: (sens: -1 | 1) => Promise<unknown>;
  actualiser?: (lignes: { ligneId: string; priceItemId: string }[]) => Promise<unknown>;
}) {
  const couts = vue.droits.couts;
  const [f, setF] = useState<FormLigne>(vide);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [comparer, setComparer] = useState(false);
  const alerte = ligne && vue.alertes_prix ? vue.alertes_prix.lignes[ligne.id] : undefined;
  // Mise à jour explicite au tarif proposé : appliquée par le serveur, puis le dialogue se ferme (ses champs seraient périmés).
  const reprendre = async (priceItemId: string) => {
    if (!ligne || !actualiser) return;
    setErreur(null); setEnvoi(true);
    try { await actualiser([{ ligneId: ligne.id, priceItemId }]); fermer(); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  useEffect(() => {
    if (!ouvert) return;
    setErreur(null); setComparer(false);
    setF(ligne ? {
      section: ligne.section, label: ligne.label, manufacturer: ligne.manufacturer ?? '', reference: ligne.reference ?? '',
      quantity: versSaisie(ligne.quantity), unit: ligne.unit, unitCost: versSaisie(ligne.unit_cost), laborHours: versSaisie(ligne.labor_hours),
      laborRateCode: ligne.labor_rate_code ?? '', rateOverride: versSaisie(ligne.rate_override),
    } : vide);
  }, [ouvert, ligne]);
  const champ = (k: keyof FormLigne) => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  const invalides = (['quantity', 'unitCost', 'laborHours', 'rateOverride'] as const).filter((k) => !saisieValide(f[k]) || (k === 'quantity' && !f.quantity.trim()));

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (invalides.length) { setErreur('Certains nombres sont illisibles (exemple attendu : 12,50).'); return; }
    const corps: Record<string, unknown> = {
      section: f.section, label: f.label, manufacturer: f.manufacturer || null, reference: f.reference || null, quantity: f.quantity,
      // Heures vides : une ligne au temps « à renseigner » le reste (jamais remplacé par 0) ; sinon 0.
      unit: f.unit || 'u', laborHours: f.laborHours.trim() === '' ? (ligne && ligne.labor_hours === null ? null : '0') : f.laborHours,
    };
    if (couts) Object.assign(corps, { unitCost: f.unitCost.trim() === '' ? null : f.unitCost, laborRateCode: f.laborRateCode || null,
      rateOverride: f.rateOverride.trim() === '' ? null : f.rateOverride });
    if (ligne && couts && versSaisie(ligne.unit_cost) === f.unitCost) delete corps.unitCost; // conserve la source et la date du prix
    setEnvoi(true);
    try { await enregistrer(corps); fermer(); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
  };
  const taux = vue.taux_horaires.filter((t) => t.active || t.code === f.laborRateCode);
  return (
    <Dialogue titre={ligne ? 'Modifier la ligne' : 'Ajouter une ligne libre'} ouvert={ouvert} fermer={fermer} large
      pied={<>
        {ligne && supprimer && <Bouton variante="danger" icone={<Trash2 aria-hidden />} onClick={async () => { await supprimer(); fermer(); }} style={{ marginRight: 'auto' }}>Supprimer</Bouton>}
        <Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" type="submit" form="form-ligne" chargement={envoi}>{ligne ? 'Enregistrer' : 'Ajouter la ligne'}</Bouton>
      </>}>
      <form id="form-ligne" className="pile" onSubmit={soumettre}>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        <div className="grille-2">
          <ChampChoix libelle="Section" options={OPTIONS_SECTIONS} value={f.section} onChange={(e) => setF({ ...f, section: e.target.value as Section })} />
          <Champ libelle="Désignation" required maxLength={500} {...champ('label')} />
          <Champ libelle="Fabricant" maxLength={120} {...champ('manufacturer')} />
          <Champ libelle="Référence" mono maxLength={120} {...champ('reference')} />
          <Champ libelle="Quantité" inputMode="decimal" required {...champ('quantity')} aria-invalid={invalides.includes('quantity') || undefined} />
          <Champ libelle="Unité" maxLength={20} {...champ('unit')} aide="u, m, ens, h, forfait…" />
          {couts && <Champ libelle="Coût unitaire d’achat (€ HT)" inputMode="decimal" {...champ('unitCost')}
            aria-invalid={invalides.includes('unitCost') || undefined}
            aide={ligne?.cost_source && versSaisie(ligne.unit_cost) === f.unitCost ? `Source : ${sourcePrix(ligne)}`
              : 'Vide = inconnu : la ligne reste « À renseigner ». Saisissez 0 s’il n’y a aucune fourniture.'} />}
          <Champ libelle="Heures unitaires" inputMode="decimal" {...champ('laborHours')} aria-invalid={invalides.includes('laborHours') || undefined}
            placeholder={ligne?.labor_hours === null ? 'À renseigner' : undefined}
            aide={ligne?.labor_hours === null ? 'Temps de pose à renseigner (ouvrage type sans temps) : la ligne reste incomplète tant qu’il est vide.' : 'Temps de main-d’œuvre pour une unité'} />
          {couts && <ChampChoix libelle="Taux horaire" vide="Aucun" value={f.laborRateCode}
            onChange={(e) => setF({ ...f, laborRateCode: e.target.value })}
            options={taux.map((t) => ({ valeur: t.code, libelle: `${t.code} — ${t.label}` }))}
            aide={vue.taux_horaires.length ? undefined : 'Aucun taux horaire : créez-en dans le catalogue.'} />}
          {couts && <Champ libelle={`Taux propre à la ligne (${vue.revision.pricing_mode === 'marge' ? 'marge sur prix de vente' : 'majoration du coût'}, %)`}
            inputMode="decimal" {...champ('rateOverride')} aria-invalid={invalides.includes('rateOverride') || undefined}
            aide={`Vide = taux du devis (${versSaisie(vue.revision.default_rate)} %)`} />}
        </div>
        {ligne && couts && alerte && (alerte.ancien || alerte.proposition || alerte.nb_fournisseurs > 1) && (
          <div className="pile devis-prix-ligne">
            <BadgesAlertePrix a={alerte} seuil={vue.alertes_prix!.seuil_mois} />
            <div className="groupe-btn">
              {alerte.proposition && actualiser && (
                <Bouton compact variante="principal" icone={<RefreshCw aria-hidden />} chargement={envoi} onClick={() => reprendre(alerte.proposition!.price_item_id)}>
                  {libelleMiseAJour(alerte.proposition)}</Bouton>
              )}
              {ligne.reference && alerte.nb_fournisseurs > 1 && (
                <Bouton compact icone={<ArrowRightLeft aria-hidden />} aria-expanded={comparer} onClick={() => setComparer(!comparer)}>
                  {comparer ? 'Masquer la comparaison' : `Comparer ${alerte.nb_fournisseurs} fournisseurs`}</Bouton>
              )}
            </div>
            {alerte.proposition && <span className="tres-petit discret">{`${alerte.proposition.source_libelle} : ${versSaisie(alerte.proposition.prix)} € HT. Le prix actuel reste tant que vous ne cliquez pas.`}</span>}
            {comparer && ligne.reference && <ListeComparaison reference={ligne.reference} actuelId={ligne.price_item_id ?? null}
              action={actualiser ? (p) => reprendre(p.id) : undefined} actionLibelle="Utiliser ce prix" />}
          </div>
        )}
        {ligne?.requirement_label && <p className="petit secondaire">Exigence liée : {ligne.requirement_label}</p>}
        {ligne && deplacer && (
          <div className="groupe-btn">
            <Bouton compact icone={<ArrowUp aria-hidden />} onClick={() => deplacer(-1)}>Monter</Bouton>
            <Bouton compact icone={<ArrowDown aria-hidden />} onClick={() => deplacer(1)}>Descendre</Bouton>
          </div>
        )}
      </form>
    </Dialogue>
  );
}

// ─── Catalogue : recherche par référence exacte puis désignation (dernier prix de chaque fournisseur) ───
export function DialogueCatalogue({ ouvert, fermer, ajouter }: { ouvert: boolean; fermer: () => void;
  ajouter: (corps: { priceItemId: string; quantity: string; section: Section }) => Promise<unknown> }) {
  const [saisie, setSaisie] = useState('');
  const [q, setQ] = useState('');
  const [quantite, setQuantite] = useState('1');
  const [section, setSection] = useState<Section>('materiel');
  const [ajoutes, setAjoutes] = useState<string[]>([]);
  const [compare, setCompare] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => { const t = window.setTimeout(() => setQ(saisie.trim()), 300); return () => window.clearTimeout(t); }, [saisie]);
  useEffect(() => { if (ouvert) { setAjoutes([]); setErreur(null); setCompare(null); } }, [ouvert]);
  const r = useQuery({ queryKey: ['prix', q, 'derniers'], enabled: ouvert,
    queryFn: () => api.get<{ exacte: boolean; elements: PrixCatalogue[] }>(`/api/prix?q=${encodeURIComponent(q)}&limite=30`) });
  const quantiteOk = saisieValide(quantite) && !!quantite.trim();
  const ajouterPrix = async (p: { id: string }) => {
    setErreur(null);
    try { await ajouter({ priceItemId: p.id, quantity: quantite, section }); setAjoutes((x) => [...x, p.id]); }
    catch (x) { setErreur(messageErreur(x)); }
  };
  return (
    <Dialogue titre="Ajouter depuis le catalogue" ouvert={ouvert} fermer={fermer} large pied={<Bouton onClick={fermer}>Fermer</Bouton>}>
      <div className="pile">
        <div className="devis-recherche-catalogue">
          <Champ libelle="Référence, désignation ou fournisseur" mono value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder="Ex. 5WG1 141-1AB23" />
          <Champ libelle="Quantité" inputMode="decimal" value={quantite} onChange={(e) => setQuantite(e.target.value)}
            aria-invalid={!quantiteOk || undefined} />
          <ChampChoix libelle="Section" options={OPTIONS_SECTIONS} value={section} onChange={(e) => setSection(e.target.value as Section)} />
        </div>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {r.isLoading && <Chargement lignes={2} />}
        {r.isError && <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />}
        {r.data && r.data.elements.length === 0 && (
          <EtatVide icone={<Search />} titre={q ? 'Aucun prix trouvé' : 'Catalogue vide'}
            texte={q ? 'Aucune référence ni désignation ne correspond. Ajoutez une ligne libre ou importez le tarif du fournisseur.'
              : 'Importez un tarif fournisseur ou saisissez des prix dans le catalogue.'} />
        )}
        {r.data && r.data.elements.length > 0 && (
          <ul className="devis-resultats">
            {q && !r.data.exacte && <li className="petit secondaire">Aucune référence exacte : résultats par désignation.</li>}
            {r.data.elements.map((p) => (
              <li key={p.id} className="pile" style={{ gap: 6 }}>
                <div className="devis-resultat">
                  <div className="pile devis-resultat-texte">
                    <span className="rangee" style={{ gap: 8 }}>
                      <span className="mono">{p.reference}</span>{p.exacte && <Badge ton="succes">Référence exacte</Badge>}
                      {p.ancien && <Badge ton="ambre">Prix ancien</Badge>}
                    </span>
                    <span>{p.label}{p.manufacturer ? ` — ${p.manufacturer}` : ''}</span>
                    <span className="tres-petit discret">{`${p.supplier_name ? `${p.supplier_name} — ` : ''}tarif du ${p.price_date ? date(p.price_date) : 'date inconnue'}`}</span>
                  </div>
                  <DetailPrix p={p} />
                  <div className="groupe-btn">
                    {(p.nb_fournisseurs ?? 0) > 1 && (
                      <Bouton compact icone={<ArrowRightLeft aria-hidden />} aria-expanded={compare === p.id} onClick={() => setCompare(compare === p.id ? null : p.id)}>
                        {compare === p.id ? 'Masquer' : `Comparer (${p.nb_fournisseurs})`}</Bouton>
                    )}
                    <Bouton compact variante={ajoutes.includes(p.id) ? 'secondaire' : 'principal'} disabled={!quantiteOk} onClick={() => ajouterPrix(p)}>
                      {ajoutes.includes(p.id) ? 'Ajouté — ajouter encore' : 'Ajouter'}</Bouton>
                  </div>
                </div>
                {compare === p.id && <ListeComparaison reference={p.reference} action={quantiteOk ? ajouterPrix : undefined} actionLibelle="Ajouter ce prix" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialogue>
  );
}

// ─── Exigences non chiffrées du dossier ───
interface Exigence { id: string; label: string; domain: string | null; detail: string | null; status: string; nature: string; uncertain: boolean;
  source_version_id: string | null; source_page: number | null; source_locator: string | null; source_excerpt: string | null; source_title: string | null }

export function DialogueExigences({ ouvert, fermer, dossierId, revisionId, ajouter }: { ouvert: boolean; fermer: () => void; dossierId: string;
  revisionId: string; ajouter: (ids: string[]) => Promise<{ creees: number; ignorees: number }> }) {
  const [choix, setChoix] = useState<string[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const q = useQuery({ queryKey: ['exigences-non-chiffrees', dossierId, revisionId], enabled: ouvert,
    queryFn: () => api.get<Exigence[]>(`/api/dossiers/${dossierId}/exigences-non-chiffrees?revision=${revisionId}`) });
  useEffect(() => { if (ouvert) { setChoix([]); setErreur(null); } }, [ouvert]);
  const tout = useMemo(() => (q.data ?? []).map((e) => e.id), [q.data]);
  return (
    <Dialogue titre="Ajouter les exigences non chiffrées" ouvert={ouvert} fermer={fermer} large
      pied={<>
        <Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" disabled={!choix.length} chargement={envoi} onClick={async () => {
          setEnvoi(true); setErreur(null);
          try { await ajouter(choix); fermer(); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
        }}>{choix.length ? `Ajouter ${pluriel(choix.length, 'ligne')}` : 'Ajouter'}</Bouton>
      </>}>
      <div className="pile">
        <p className="petit secondaire">Exigences du dossier au statut « Manquant », « À clarifier » ou « Inclus et chiffré » qui ne sont reliées à aucune ligne de cette révision.
          Chaque exigence devient une ligne dont le prix reste « À renseigner ».</p>
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
        {q.isLoading && <Chargement lignes={3} />}
        {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
        {q.data && q.data.length === 0 && <EtatVide titre="Aucune exigence à chiffrer" texte="Toutes les exigences concernées sont déjà reliées à une ligne de cette révision." />}
        {q.data && q.data.length > 0 && (
          <>
            <label className="case"><input type="checkbox" checked={choix.length === tout.length}
              onChange={(e) => setChoix(e.target.checked ? tout : [])} /> Tout sélectionner ({tout.length})</label>
            <ul className="devis-exigences">
              {q.data.map((e) => (
                <li key={e.id} className="devis-exigence">
                  <label className="case" style={{ paddingTop: 0 }}>
                    <input type="checkbox" checked={choix.includes(e.id)}
                      onChange={(x) => setChoix(x.target.checked ? [...choix, e.id] : choix.filter((c) => c !== e.id))} />
                    <span className="pile" style={{ gap: 4 }}>
                      <strong>{e.label}</strong>
                      <span className="rangee" style={{ gap: 6 }}>
                        <BadgeStatutExigence statut={e.status} />
                        {e.uncertain && <Badge ton="ambre">À vérifier</Badge>}
                        {e.domain && <span className="tres-petit discret">{e.domain}</span>}
                      </span>
                    </span>
                  </label>
                  {e.source_excerpt && <p className="extrait">{e.source_excerpt}</p>}
                  <div className="rangee" style={{ gap: 8 }}>
                    {e.source_title || e.source_version_id
                      ? <Source versionId={e.source_version_id} page={e.source_page} titre={e.source_title ?? 'Pièce du dossier'} localisation={e.source_locator} />
                      : <span className="tres-petit discret">Source non renseignée</span>}
                    <Link className="petit" to={`/etudes/${dossierId}/exigences`}>Voir dans les exigences du dossier</Link>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Dialogue>
  );
}

// ─── Dérogation à l'émission ───
export function DialogueDerogation({ ouvert, fermer, lignes, emettre }: { ouvert: boolean; fermer: () => void;
  lignes: { id: string; label: string; motifs: string[] }[]; emettre: (motif: string) => Promise<unknown> }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { if (ouvert) { setMotif(''); setErreur(null); } }, [ouvert]);
  const assez = motif.trim().length >= 15;
  return (
    <Dialogue titre="Émettre malgré des lignes à renseigner" ouvert={ouvert} fermer={fermer}
      pied={<>
        <Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" disabled={!assez} chargement={envoi} onClick={async () => {
          setEnvoi(true); setErreur(null);
          try { await emettre(motif.trim()); fermer(); } catch (x) { setErreur(messageErreur(x)); } finally { setEnvoi(false); }
        }}>Émettre avec dérogation</Bouton>
      </>}>
      <div className="pile">
        <Alerte ton="ambre" titre={`${pluriel(lignes.length, 'ligne')} à renseigner`}>
          Le devis émis sera figé avec ces lignes affichées « À renseigner » au client. La dérogation est tracée : votre nom, la date, le motif et les lignes concernées.
        </Alerte>
        <ul className="devis-liste-simple">
          {lignes.map((l) => <li key={l.id}><strong>{l.label}</strong> <span className="petit secondaire">— {l.motifs.map((m) => LIBELLES_MOTIFS[m] ?? m).join(' ; ')}</span></li>)}
        </ul>
        <ChampTexte libelle="Motif de la dérogation" value={motif} onChange={(e) => setMotif(e.target.value)} rows={3} maxLength={2000}
          aide={assez ? `${motif.trim().length} caractères` : `15 caractères minimum (${motif.trim().length}/15)`} required />
        {erreur && <Alerte ton="danger">{erreur}</Alerte>}
      </div>
    </Dialogue>
  );
}

// ─── Nouvelle variante ───
export function DialogueVariante({ ouvert, fermer, creer }: { ouvert: boolean; fermer: () => void; creer: (nom: string) => Promise<unknown> }) {
  const [nom, setNom] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  useEffect(() => { if (ouvert) { setNom(''); setErreur(null); } }, [ouvert]);
  return (
    <Dialogue titre="Créer une variante" ouvert={ouvert} fermer={fermer}
      pied={<><Bouton onClick={fermer}>Annuler</Bouton>
        <Bouton variante="principal" type="submit" form="form-variante" disabled={!nom.trim()}>Créer la variante</Bouton></>}>
      <form id="form-variante" className="pile" onSubmit={async (e) => {
        e.preventDefault();
        try { await creer(nom.trim()); fermer(); } catch (x) { setErreur(messageErreur(x)); }
      }}>
        <p className="petit secondaire">La variante reprend toutes les lignes et paramètres de la révision affichée, sous un autre libellé (ex. « Option supervision »).</p>
        <Champ libelle="Libellé de la variante" value={nom} maxLength={80} onChange={(e) => setNom(e.target.value)} erreur={erreur} required />
      </form>
    </Dialogue>
  );
}
